import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Order } from '../types';
import { 
  CheckCircle2, Copy, Check, ShieldCheck, Zap, 
  Clock, AlertTriangle, MessageSquare, Star, ArrowRight 
} from 'lucide-react';
import { WarrantyBadge } from '../components/common/Badge';

export const PaymentStatus: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [accepting, setAccepting] = useState(false);

  // Review modal
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  // Dispute modal
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState('Credentials invalid or non-working');
  const [disputeEvidence, setDisputeEvidence] = useState('');
  const [disputeSubmitted, setDisputeSubmitted] = useState(false);

  const fetchOrder = async () => {
    try {
      const res = await fetch(`/api/orders/${orderId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.order) {
          setOrder(data.order);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const handleCopyCredentials = () => {
    if (order?.deliveredCredentials) {
      navigator.clipboard.writeText(order.deliveredCredentials);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleAcceptOrder = async () => {
    setAccepting(true);
    try {
      const res = await fetch(`/api/orders/${orderId}/accept`, { method: 'POST' });
      if (res.ok) {
        await fetchOrder();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setAccepting(false);
    }
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          rating: reviewRating,
          comment: reviewComment
        })
      });
      if (res.ok) {
        setReviewSubmitted(true);
        setReviewOpen(false);
        await fetchOrder();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmitDispute = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/disputes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          reason: disputeReason,
          evidence_text: disputeEvidence
        })
      });
      if (res.ok) {
        setDisputeSubmitted(true);
        setDisputeOpen(false);
        await fetchOrder();
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center text-xs">
        Loading order status...
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h2 className="text-xl font-bold">Order Not Found</h2>
        <Link to="/buyer/orders" className="mt-4 inline-block text-brand-blue font-bold">View My Orders</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      
      {/* Top Banner Status */}
      <div className="p-8 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder shadow-xl text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-emerald-500/15 text-emerald-500 mx-auto flex items-center justify-center shadow-lg">
          <CheckCircle2 className="w-8 h-8" />
        </div>

        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
            Payment Verified & Processed
          </span>
          <h1 className="text-2xl sm:text-3xl font-black text-brand-lightText dark:text-brand-darkText mt-1">
            Order #{order.order_number}
          </h1>
          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
            Total Paid: <strong>${(order.total_cents / 100).toFixed(2)} USD</strong> • Awarded: <strong>+{order.points_rewarded} VaultPoints</strong>
          </p>
        </div>

        <div className="flex items-center justify-center gap-3 pt-2">
          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
            order.status === 'completed'
              ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/30'
              : order.status === 'delivered'
              ? 'bg-blue-500/10 text-brand-blue border border-brand-blue/30'
              : 'bg-amber-500/10 text-amber-600 border border-amber-500/30'
          }`}>
            Status: {order.status}
          </span>
          <WarrantyBadge hours={48} />
        </div>
      </div>

      {/* Instant Digital Delivery Key Box */}
      {order.deliveredCredentials && (
        <div className="p-6 sm:p-8 rounded-3xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-blue/40 shadow-glow-blue space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-brand-blue dark:text-brand-accent">
              <Zap className="w-5 h-5 fill-current" />
              <h3 className="font-extrabold text-base">Your Decrypted Digital Credentials</h3>
            </div>
            <button
              onClick={handleCopyCredentials}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-brand-blue text-white hover:bg-brand-hover transition-all"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copied ? 'Copied to Clipboard' : 'Copy'}</span>
            </button>
          </div>

          <div className="p-4 rounded-2xl bg-white dark:bg-black/60 font-mono text-xs sm:text-sm text-brand-lightText dark:text-emerald-400 border border-brand-lightBorder dark:border-brand-darkBorder break-all select-all">
            {order.deliveredCredentials}
          </div>

          <p className="text-[11px] text-brand-lightSecText dark:text-brand-darkSecText leading-relaxed">
            * Please test and change passwords/recovery credentials immediately if purchasing a full-access account.
          </p>
        </div>
      )}

      {/* Boosting Order Details if applicable */}
      {order.boosting && (
        <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-4">
          <h3 className="font-extrabold text-base text-brand-lightText dark:text-brand-darkText flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />
            <span>Boosting Progress Tracker</span>
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
            <div className="p-3 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated">
              <span className="text-brand-lightSecText block">Service:</span>
              <strong className="capitalize">{order.boosting.service_type.replace('_', ' ')}</strong>
            </div>
            <div className="p-3 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated">
              <span className="text-brand-lightSecText block">Path:</span>
              <strong>{order.boosting.current_rank} ➔ {order.boosting.target_rank}</strong>
            </div>
            <div className="p-3 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated">
              <span className="text-brand-lightSecText block">Progress:</span>
              <strong className="text-brand-blue">{order.boosting.progress_percent}%</strong>
            </div>
            <div className="p-3 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated">
              <span className="text-brand-lightSecText block">Status:</span>
              <strong className="capitalize">{order.boosting.status.replace('_', ' ')}</strong>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-brand-lightSec dark:bg-brand-darkElevated h-3 rounded-full overflow-hidden">
            <div
              className="bg-brand-blue h-full rounded-full transition-all duration-500"
              style={{ width: `${order.boosting.progress_percent}%` }}
            />
          </div>

          {order.boosting.booster_notes && (
            <div className="p-3 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated text-xs">
              <span className="font-bold block mb-1">Booster Notes:</span>
              <p className="text-brand-lightSecText">{order.boosting.booster_notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Buyer Action Controls */}
      <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-4">
        <h3 className="font-extrabold text-base text-brand-lightText dark:text-brand-darkText">
          Order Actions & Buyer Protection
        </h3>

        <div className="flex flex-wrap gap-3">
          {order.status !== 'completed' && order.status !== 'refunded' && (
            <button
              onClick={handleAcceptOrder}
              disabled={accepting}
              className="px-5 py-2.5 rounded-xl font-bold text-xs text-white bg-emerald-600 hover:bg-emerald-700 transition-colors flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              <span>{accepting ? 'Releasing Funds...' : 'Accept Order (Confirm Working)'}</span>
            </button>
          )}

          {!order.review && (order.status === 'delivered' || order.status === 'completed') && (
            <button
              onClick={() => setReviewOpen(true)}
              className="px-5 py-2.5 rounded-xl font-bold text-xs bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 border border-amber-500/30 flex items-center gap-2"
            >
              <Star className="w-4 h-4" />
              <span>Leave Verified Review</span>
            </button>
          )}

          {order.status !== 'refunded' && !order.dispute && (
            <button
              onClick={() => setDisputeOpen(true)}
              className="px-5 py-2.5 rounded-xl font-bold text-xs bg-rose-500/10 text-rose-600 hover:bg-rose-500/20 border border-rose-500/30 flex items-center gap-2"
            >
              <AlertTriangle className="w-4 h-4" />
              <span>Report Issue / Open Dispute</span>
            </button>
          )}

          <Link
            to="/buyer/messages"
            className="px-5 py-2.5 rounded-xl font-bold text-xs bg-brand-lightSec dark:bg-brand-darkElevated text-brand-lightText dark:text-brand-darkText hover:text-brand-blue border border-brand-lightBorder dark:border-brand-darkBorder flex items-center gap-2"
          >
            <MessageSquare className="w-4 h-4" />
            <span>Chat With Seller</span>
          </Link>
        </div>
      </div>

      {/* Review Modal */}
      {reviewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder rounded-3xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-bold text-lg">Leave a Verified Review</h3>
            <form onSubmit={handleSubmitReview} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold mb-1">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => setReviewRating(s)}
                      className={`p-2 rounded-xl border flex items-center justify-center ${
                        reviewRating >= s ? 'bg-amber-500 text-white' : 'border-brand-lightBorder text-slate-400'
                      }`}
                    >
                      <Star className="w-4 h-4 fill-current" />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1">Your Feedback</label>
                <textarea
                  required
                  rows={3}
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Share details on delivery speed and product quality..."
                  className="w-full p-3 text-xs rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setReviewOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-brand-lightSecText"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-xs font-bold text-white bg-brand-blue rounded-xl"
                >
                  Submit Review
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dispute Modal */}
      {disputeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder rounded-3xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-2 text-rose-500">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="font-bold text-lg">Open 72-Hour Warranty Dispute</h3>
            </div>
            <form onSubmit={handleSubmitDispute} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold mb-1">Dispute Reason</label>
                <select
                  value={disputeReason}
                  onChange={(e) => setDisputeReason(e.target.value)}
                  className="w-full p-2.5 text-xs rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder"
                >
                  <option value="Credentials invalid or non-working">Credentials invalid or non-working</option>
                  <option value="Item does not match listing description">Item does not match listing description</option>
                  <option value="Account locked or banned upon first login">Account locked or banned upon first login</option>
                  <option value="Seller unresponsive for manual delivery">Seller unresponsive for manual delivery</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1">Evidence Description & Screenshots</label>
                <textarea
                  required
                  rows={4}
                  value={disputeEvidence}
                  onChange={(e) => setDisputeEvidence(e.target.value)}
                  placeholder="Explain what happened when testing the purchase. GameVault support will review within 12 hours..."
                  className="w-full p-3 text-xs rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setDisputeOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-brand-lightSecText"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-xs font-bold text-white bg-rose-600 rounded-xl"
                >
                  Submit Dispute
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
